import React, { useCallback } from 'react';
import styled from '@emotion/styled';
import { Color } from '@diritto/common/style/color';
import { Link } from 'ui-components/common/Link';
import { ProfileImage } from 'app-components/accounts/profiles/ProfileImage';
import { FontWeight, Typography } from '@diritto/common/style/typography';
import { getTextLineStyle } from 'styles/common';
import { RelativeDateText } from 'ui-components/common/RelativeDateText';
import { RelayType } from '@diritto/common/content/constants';
import { EpisodeStatus } from '@diritto/common/episode/constants';
import { buildEpisodeReplyUrl } from 'utils/url';
import { ContentRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { EpisodeRelayDto } from '@diritto/common/relay/dtos/episode-relay.dtos';
import { ProfileLiteRelayDto } from '@diritto/common/relay/dtos/profile-lite-relay.dtos';
import { EpisodeReplyRelayDto } from '@diritto/common/relay/dtos/episode-reply-relay.dto';
import { TrackerExtraInfo, TrackingEventType } from '@diritto/common/tracker/tracking-event.constant';
import { useTrackingState } from 'contexts/TrackingProvider';
import { css } from '@emotion/react';

export const SectionReply = ({
  reply,
  contents,
  episodes,
  profileLites,
  descriptionLineCount,
  onClick,
  className,
  trackerExtraInfo,
}: {
  reply: EpisodeReplyRelayDto;
  contents: Record<number, ContentRelayDto>;
  episodes: Record<number, EpisodeRelayDto>;
  profileLites: Record<number, ProfileLiteRelayDto>;
  descriptionLineCount: number;
  onClick?: () => void;
  className?: string;
  trackerExtraInfo: TrackerExtraInfo;
}) => {
  const { tracker } = useTrackingState();

  const handleOnClickTracker = useCallback(() => {
    tracker.track(TrackingEventType.ClickToReply, {
      replyId: reply.id,
      trackerExtraInfo,
    });
  }, [reply.id, tracker, trackerExtraInfo]);

  const handleOnClick = useCallback(() => {
    handleOnClickTracker();
    onClick?.();
  }, [handleOnClickTracker, onClick]);

  if (!reply || reply.isDeleted) {
    return null;
  }
  const profile = reply.profileId && profileLites[reply.profileId];
  if (!profile || profile.isDeleted) {
    return null;
  }
  const content = contents[reply.contentId];

  if (!content || content.relayType !== RelayType.Public) {
    return null;
  }
  const episode = episodes[reply.episodeId];
  if (!episode || episode.status !== EpisodeStatus.Public) {
    return null;
  }

  const link = buildEpisodeReplyUrl(reply);

  return (
    <StyledReplyWrapper key={reply.id} onClick={handleOnClick} className={className}>
      <StyledProfileLink href={`/accounts/profiles/${profile.id}`}>
        <StyledProfileImage file={profile.profileImage} defaultImageKey={profile.nickname} size={24} />
        <StyledProfileNickname>{profile.nickname}</StyledProfileNickname>
      </StyledProfileLink>
      <StyledLink href={link}>
        <StyledDescription descriptionLineCount={descriptionLineCount}>{reply.description}</StyledDescription>
        <StyledContentEpisodeTitle>
          <span>{content.title}</span>
          <StyledSlash>/</StyledSlash>
          <span>{episode.title}</span>
        </StyledContentEpisodeTitle>
      </StyledLink>
      <StyledReplyCreateAt date={reply.createdAt} />
    </StyledReplyWrapper>
  );
};

const StyledReplyWrapper = styled.div`
  padding: 16px;
  width: 100%;
  height: 210px;

  display: flex;
  flex-direction: column;
  gap: 8px;

  background-color: ${Color.Gray100};
  border: 1px solid rgba(0, 0, 0, 0.05);
  border-radius: 4px;
`;

const StyledProfileLink = styled(Link)`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  gap: 8px;
`;

const StyledProfileImage = styled(ProfileImage)`
  flex-shrink: 0;
`;
const StyledSlash = styled.span`
  color: ${Color.Gray400};
  margin: 0 4px;
`;

const StyledProfileNickname = styled.div`
  flex-shrink: 1;
  color: ${Color.Gray700};
  ${Typography.XXXSmall};
  font-weight: ${FontWeight.Medium};
  ${getTextLineStyle(1)};
`;

const StyledDescription = styled.div(
  ({ descriptionLineCount }: { descriptionLineCount: number }) => css`
    color: ${Color.Black};
    ${Typography.XXSmall};
    font-weight: ${FontWeight.Medium};
    ${getTextLineStyle(descriptionLineCount)};
  `,
);

const StyledContentEpisodeTitle = styled.div`
  color: ${Color.Gray700};
  ${Typography.XXXSmall};
  font-weight: ${FontWeight.Medium};
  ${getTextLineStyle(2)};
`;

const StyledReplyCreateAt = styled(RelativeDateText)`
  margin-top: auto;
  color: ${Color.Gray500};
  ${Typography.XXXSmall};
  font-weight: ${FontWeight.Regular};
`;

const StyledLink = styled(Link)`
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
